import React, { useRef, useState } from "react";
import { FileText, Download, Archive, Upload, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { documentos as api, archivos, fotos } from "../../api/modulos.service";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";

/**
 * DOCUMENTOS DE LA RESIDENCIAL.
 *
 * El reglamento, las actas, los estados de cuenta. Hoy viven en un grupo de
 * WhatsApp, donde a las tres semanas ya nadie los encuentra y el vecino nuevo
 * simplemente no los tiene.
 *
 * Nada se sirve por una dirección fija: al tocar «Abrir» se pide una URL
 * firmada que dura quince minutos. Reenviar el enlace no funciona, que es
 * justo lo que se quiere de un estado de cuenta.
 */

const FORM_VACIO = { title: "", description: "", category: "reglamento", audience: "todos" };

const CATEGORIAS = [
  { valor: "reglamento", etiqueta: "Reglamento" },
  { valor: "acta", etiqueta: "Acta" },
  { valor: "estado_cuenta", etiqueta: "Estado de cuenta" },
  { valor: "circular", etiqueta: "Circular" },
  { valor: "plano", etiqueta: "Plano" },
  { valor: "contrato", etiqueta: "Contrato" },
  { valor: "otro", etiqueta: "Otro" },
];

const peso = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fecha = (f) =>
  new Date(f).toLocaleDateString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "medium" });

export default function Documentos({ rol }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [archivo, setArchivo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const campoArchivo = useRef(null);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(
    () => api.listar(busqueda ? { buscar: busqueda } : {}),
    [busqueda]
  );
  const lista = datos?.documents || [];
  const esAdmin = rol === "admin";

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const abrir = async (doc) => {
    setError("");
    try {
      const { url } = await fotos.urlDeLectura(doc.key);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError("No se pudo abrir el documento");
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    if (!archivo) {
      setError("Elija un archivo");
      return;
    }
    setGuardando(true);
    try {
      // `recursoId` aleatorio: el documento todavía no existe en la base, así
      // que no tiene identificador con el que agrupar su archivo en S3.
      const subido = await archivos.subir(archivo, {
        ambito: "documentos",
        recursoId: Math.random().toString(36).slice(2, 14),
      });
      await api.crear({
        ...form,
        key: subido.key,
        originalName: subido.originalName,
        contentType: subido.contentType,
        size: subido.size,
      });
      setAbierta(false);
      setForm(FORM_VACIO);
      setArchivo(null);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "No se pudo publicar el documento");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Documentos"
        subtitulo="El reglamento y las actas, donde siempre se encuentran."
        accion={esAdmin ? "Publicar documento" : null}
        alAccionar={() => setAbierta(true)}
        icono={Upload}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar por título…" />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>{busqueda ? "Ningún documento con ese título." : "Todavía no hay documentos publicados."}</Vacio>
      ) : (
        <div style={est.rejilla}>
          {lista.map((d) => (
            <div key={d._id} style={est.tarjeta}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <FileText size={18} style={{ color: "var(--gold)", flexShrink: 0 }} />
                <h3 style={est.tarjetaTitulo}>{d.title}</h3>
              </div>
              <div style={est.tarjetaDato}>
                {(CATEGORIAS.find((c) => c.valor === d.category) || {}).etiqueta || d.category}
                {d.size ? ` · ${peso(d.size)}` : ""} · {fecha(d.createdAt)}
              </div>
              {d.description && (
                <div style={{ ...est.tarjetaDato, display: "block" }}>{d.description}</div>
              )}
              <div style={est.tarjetaAcciones}>
                <Button size="sm" variant="outline" leftIcon={Download} onClick={() => abrir(d)}>
                  Abrir
                </Button>
                {esAdmin && (
                  <Button size="sm" variant="ghost" leftIcon={Archive} onClick={() => api.archivar(d._id).then(recargar)}>
                    Archivar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Publicar un documento"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>
              Publicar
            </Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Título" value={form.title} onChange={cambiar("title")} required />
        <Area etiqueta="Descripción (opcional)" value={form.description} onChange={cambiar("description")} />
        <div style={est.fila}>
          <Seleccion etiqueta="Tipo" value={form.category} onChange={cambiar("category")} opciones={CATEGORIAS} />
          <Seleccion
            etiqueta="Para quién"
            value={form.audience}
            onChange={cambiar("audience")}
            opciones={[
              { valor: "todos", etiqueta: "Toda la residencial" },
              { valor: "residentes", etiqueta: "Sólo residentes" },
              { valor: "administracion", etiqueta: "Sólo administración" },
            ]}
          />
        </div>

        <div style={est.campo}>
          <span style={est.etiqueta}>Archivo (PDF o imagen)</span>
          <input
            ref={campoArchivo}
            type="file"
            accept="application/pdf,image/*"
            style={est.control}
            onChange={(e) => setArchivo(e.target.files?.[0] || null)}
          />
          {archivo && (
            <span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
              {archivo.name} · {peso(archivo.size)}
            </span>
          )}
        </div>
      </Ventana>
    </div>
  );
}
